import * as api from 'src/api/ranoScoring';

import { getUrlParams } from '../helpers';
import { runToast } from '../tools';

export type RanoCalculateRequest = {
  leptomeningealDisease?: boolean;
  overrideResponse?: string;
};

export const runRanoCalculate = async (args: api.CalculateRanoArgs) =>
  (await api.calculateRanoScoring(args)).data;

// `readerTaskViewer` has no `:subTaskId` and a reading switch lands on it
// transiently, so an unresolved route is normal. It throws before the toast:
// the reader-facing message belongs to a backend failure only.
export const calculateRanoForCurrentReading = async ({
  leptomeningealDisease,
  overrideResponse,
}: RanoCalculateRequest = {}) => {
  const params = getUrlParams();

  if (!params?.id || !params.taskId || !params.subTaskId) {
    throw new Error('Cannot calculate RANO: the reading route is not resolved');
  }

  /**
   * Send the reader's override only when there is one
   *
   * Plain terms
   * -----------
   * A reader may override the calculated RANO response. When the form has no
   * override, the request must not mention one at all, so the backend keeps
   * its own calculated result.
   *
   * Technical
   * ---------
   * The RPC payload is cast in `registerFormHandlers`, not validated, so
   * `null`, `''` or a non-string can arrive. Only a non-empty string is
   * forwarded; anything else omits the key instead of sending
   * `"overrideResponse": null`.
   */
  const hasOverrideResponse =
    typeof overrideResponse === 'string' && overrideResponse !== '';

  try {
    return await runRanoCalculate({
      clinicalTrialId: params.id,
      parentTaskId: params.taskId,
      taskId: params.subTaskId,
      body: {
        leptomeningealDisease: !!leptomeningealDisease,
        ...(hasOverrideResponse && { overrideResponse }),
      },
    });
  } catch (error) {
    runToast('error', 'Failed to calculate RANO response');
    console.error('Calculate RANO error', error);

    throw error;
  }
};
